/**
 * reportResolve.js — the code→props resolver for the standalone render route.
 *
 * Given a (code, level, type), produce the EXACT props the report component
 * receives under the report studio (ReportPage) today, so a report renders
 * identically at /report-render as it does at /report. Reuses the shared
 * districtNames helpers + stateFipsToName — no reinvention.
 *
 * STAGE 1 scope: political × CD only. Other types/levels return an error until
 * the expansion stage (kept explicit so the route fails loud, not silently).
 *
 * CD code forms accepted:
 *   • 4-digit GEOID  "3712"  (state FIPS 37 + district 12)   ← canonical
 *   • abbr-district  "NC-12" / "NC-1"                        ← app's areaCode form
 */
import { stateFipsToName, normalizeId } from "./idUtils";
import { FIPS_TO_ABBR, ABBR_TO_FIPS, SEATS_BY_STATE, buildDistrictName } from "./districtNames";

// Report type → valid geo levels (matches desktop ReportPage's area-type options).
// political = CD only; client/example = state / state-senate / state-house / CD.
// (counties / zipcodes / media_market are map-only, never report levels.)
export const TYPE_LEVELS = {
  political: ["cd"],
  client:    ["cd", "states", "statesenate", "statehouse"],
  example:   ["cd", "states", "statesenate", "statehouse"],
};

const SEAT_JSON = {
  statesenate: "/usmaps/us_statelegsenate_simplified.json",
  statehouse:  "/usmaps/us_stateleghouse_simplified.json",
};

/**
 * All congressional districts as pickable options — built deterministically from
 * SEATS_BY_STATE + buildDistrictName (no FGB fetch). Mirrors desktop's district
 * dropdown (districts 1..seatCount, padded). `code` is the 4-digit GEOID the
 * resolver + /api/report/pdf/ accept. `search` matches name / "NC-12" / "3712".
 */
export function listCdOptions() {
  const out = [];
  for (const fips of Object.keys(SEATS_BY_STATE)) {
    const seats = SEATS_BY_STATE[fips] || 0;
    const abbr = FIPS_TO_ABBR[fips] || fips;
    for (let d = 1; d <= seats; d++) {
      const padded = String(d).padStart(2, "0");
      const code = `${fips}${padded}`;
      const name = buildDistrictName(fips, padded);
      out.push({ code, name, state: abbr, search: `${name} ${abbr}-${padded} ${code}`.toLowerCase() });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

// → { stateFips, districtPadded } | null
function parseCdCode(code) {
  const raw = String(code || "").trim().toUpperCase();

  // "NC-12" / "NC-1"
  let m = raw.match(/^([A-Z]{2})-(\d{1,2})$/);
  if (m) {
    const fips = ABBR_TO_FIPS[m[1]];
    return fips ? { stateFips: fips, districtPadded: m[2].padStart(2, "0") } : null;
  }

  // 4-digit GEOID "3712"
  m = raw.match(/^(\d{2})(\d{2})$/);
  if (m) return { stateFips: m[1], districtPadded: m[2] };

  return null;
}

// CD: parse 4-digit GEOID / "NC-12" → props (mirrors ReportPage.addDistrict for a CD).
function cdProps(code, type) {
  const parsed = parseCdCode(code);
  if (!parsed) return { error: `Unrecognised CD code "${code}" — expected "3712" or "NC-12".` };
  const { stateFips, districtPadded } = parsed;
  const state = FIPS_TO_ABBR[stateFips];
  const stateName = stateFipsToName[stateFips];
  if (!state || !stateName) return { error: `Unknown state FIPS "${stateFips}".` };
  return {
    props: {
      districtName: buildDistrictName(stateFips, districtPadded),
      state, stateFips, stateName,
      district: Number(districtPadded),
      areaCode: `${state}-${districtPadded}`,
      areaType: "cd", reportType: type,
    },
  };
}

// State: code is a 2-digit FIPS (mirrors ReportPage: areaCode = selState, district null).
function stateProps(code, type) {
  const stateFips = String(code || "").padStart(2, "0");
  const state = FIPS_TO_ABBR[stateFips];
  const stateName = stateFipsToName[stateFips];
  if (!state || !stateName) return { error: `Unknown state FIPS "${code}".` };
  return {
    props: {
      districtName: stateName,
      state, stateFips, stateName,
      district: null, areaCode: stateFips,
      areaType: "states", reportType: type,
    },
  };
}

/**
 * SYNC resolver — political/CD and client|example × (cd|states). Seat levels
 * (statesenate/statehouse) need a JSON load → use resolveReportPropsAsync.
 * @returns {{ props } | { error }}
 */
export function resolveReportProps({ code, level = "cd", type = "political" }) {
  const levels = TYPE_LEVELS[type];
  if (!levels) return { error: `Unknown report type "${type}".` };
  if (!levels.includes(level)) return { error: `Report type "${type}" does not support level "${level}".` };
  if (level === "cd") return cdProps(code, type);
  if (level === "states") return stateProps(code, type);
  return { error: `Level "${level}" requires async resolution (use resolveReportPropsAsync).` };
}

/**
 * ASYNC seat resolver — statesenate/statehouse. Loads the stateleg GeoJSON (as
 * ReportPage.loadSeatOptions does), finds the seat whose normalizeId === code,
 * and builds "{state} State Senate/House — District {num}".
 */
export async function resolveSeatProps({ code, level, type }) {
  const levels = TYPE_LEVELS[type];
  if (!levels || !levels.includes(level)) return { error: `Report type "${type}" does not support level "${level}".` };
  const raw = String(code || "").trim();
  const stateFips = raw.slice(0, 2);
  const state = FIPS_TO_ABBR[stateFips];
  const stateName = stateFipsToName[stateFips];
  if (!state || !stateName) return { error: `Unknown state FIPS in seat code "${code}".` };
  try {
    const res = await fetch((process.env.PUBLIC_URL || "") + SEAT_JSON[level]);
    // Defensive: if /usmaps/ is mis-served (SPA catch-all → index.html, or 404),
    // fail FAST with a clear message instead of a cryptic res.json() parse error.
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (!res.ok || !ct.includes("json")) {
      return { error: `Seat map data not served as JSON (HTTP ${res.status}, "${ct || "no content-type"}") — check /usmaps/ static serving.` };
    }
    const data = await res.json();
    const feats = (data.features || []).filter((f) => {
      const props = f.properties || {};
      const sf = String(props.STATE ?? props.STATEFP ?? "").padStart(2, "0");
      return sf === stateFips;
    });
    const match = feats.find((f) => String(normalizeId(level, f.properties || {})) === raw);
    if (!match) return { error: `Seat "${code}" not found in ${level}.` };
    const p = match.properties || {};
    const name = p.NAME || p.NAMELSAD || `District ${p.SLDUST || p.SLDLST || ""}`.trim();
    const numMatch = String(name).match(/(\d+)/) || raw.replace(/\D/g, "").slice(-3).match(/(\d+)/);
    const num = numMatch ? Number(numMatch[1]) : name;
    const chamber = level === "statesenate" ? "State Senate" : "State House";
    return {
      props: {
        districtName: `${stateName} ${chamber} — District ${num}`,
        state, stateFips, stateName,
        district: raw, areaCode: raw,
        areaType: level, reportType: type,
      },
    };
  } catch (e) {
    return { error: `Failed to load ${level} seats: ${e.message}` };
  }
}

/** Router-facing resolver: seat levels → async JSON load; others → sync. */
export async function resolveReportPropsAsync(args) {
  if (args.level === "statesenate" || args.level === "statehouse") return resolveSeatProps(args);
  return resolveReportProps(args);
}

/**
 * Seat options for the picker (statesenate/statehouse) — loads the stateleg
 * GeoJSON, filters by state, returns [{ code, label }] sorted by district number.
 * Mirrors ReportPage.loadSeatOptions; code === the normalizeId the resolver expects.
 */
export async function listSeatOptions(level, stateFips) {
  if (level !== "statesenate" && level !== "statehouse") return [];
  const fips = String(stateFips || "").padStart(2, "0");
  const res = await fetch((process.env.PUBLIC_URL || "") + SEAT_JSON[level]);
  const data = await res.json();
  const feats = (data.features || []).filter((f) => {
    const p = f.properties || {};
    return String(p.STATE ?? p.STATEFP ?? "").padStart(2, "0") === fips;
  });
  return feats
    .map((f) => {
      const p = f.properties || {};
      const code = String(normalizeId(level, p));
      const label = p.NAME || p.NAMELSAD || `District ${p.SLDUST || p.SLDLST || ""}`.trim();
      const m = String(label).match(/(\d+)/);
      return { code, label, num: m ? Number(m[1]) : null };
    })
    .sort((a, b) => (a.num != null && b.num != null ? a.num - b.num : String(a.label).localeCompare(String(b.label))))
    .map(({ code, label }) => ({ code, label }));
}
